import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { imageUrl } from "../../utils/imageUrl";

function normalizeList(value) {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (typeof value === "object") {
    return Object.values(value).flat().filter(Boolean);
  }

  return [];
}

function normalizeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function placementMatches(campaign, placement) {
  const campaignPlacement = campaign?.placement || "";

  if (!campaignPlacement) return true;
  if (campaignPlacement === "global") return true;
  return campaignPlacement === placement;
}

function getCampaignsByType(campaigns, type, placement = "home") {
  return normalizeList(campaigns?.[type])
    .filter((item) => item && item.is_active !== false)
    .filter((item) => placementMatches(item, placement))
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
}

function getImage(campaign) {
  const mobileImage = campaign.mobile_image;
  const image = campaign.image;
  const selected = typeof window !== "undefined" && window.innerWidth <= 640 && mobileImage
    ? mobileImage
    : image || mobileImage || "";

  return selected ? imageUrl(selected) : "";
}

function getStorageKey(campaign) {
  const rules = normalizeObject(campaign.display_rules);
  return rules.storage_key || `jooti_campaign_${campaign.type}_${campaign.id}`;
}

function shouldShowCampaign(campaign) {
  const rules = normalizeObject(campaign.display_rules);
  const days = Number(rules.show_once_per_days || 0);

  if (!days) return true;

  try {
    const key = getStorageKey(campaign);
    const raw = localStorage.getItem(key);

    if (!raw) return true;

    const lastSeen = Number(raw);
    const now = Date.now();
    const diff = now - lastSeen;
    const limit = days * 24 * 60 * 60 * 1000;

    return diff > limit;
  } catch {
    return true;
  }
}

function markCampaignSeen(campaign) {
  try {
    localStorage.setItem(getStorageKey(campaign), String(Date.now()));
  } catch {
    return null;
  }

  return null;
}

function CampaignButton({ campaign, className = "" }) {
  if (!campaign.button_text || !campaign.button_link) return null;

  const isExternal = /^https?:\/\//i.test(campaign.button_link);

  if (isExternal) {
    return (
      <a href={campaign.button_link} className={className} target="_blank" rel="noreferrer">
        {campaign.button_text}
      </a>
    );
  }

  return (
    <Link to={campaign.button_link} className={className}>
      {campaign.button_text}
    </Link>
  );
}

function CampaignTopbar({ campaigns, placement = "global" }) {
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const topbars = getCampaignsByType(campaigns, "topbar", placement).filter((campaign) => !hiddenKeys.includes(getStorageKey(campaign)));

  if (!topbars.length) return null;

  const campaign = topbars[0];
  const style = normalizeObject(campaign.style_settings);
  const tone = style.tone || "pink";

  const closeTopbar = () => {
    markCampaignSeen(campaign);
    setHiddenKeys((prev) => [...prev, getStorageKey(campaign)]);
  };

  return (
    <div className={`campaign-topbar campaign-tone-${tone}`}>
      <div className="site-container campaign-topbar-inner">
        <div className="campaign-topbar-copy">
          <strong>{campaign.title}</strong>
          {campaign.subtitle ? <span>{campaign.subtitle}</span> : null}
        </div>

        <div className="campaign-topbar-actions">
          <CampaignButton campaign={campaign} className="campaign-topbar-link" />
          {style.show_close !== false ? (
            <button type="button" className="campaign-close-button" onClick={closeTopbar} aria-label="بستن اعلان">
              ×
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CampaignHero({ campaigns, placement = "home" }) {
  const heroes = getCampaignsByType(campaigns, "hero", placement);
  if (!heroes.length) return null;

  const campaign = heroes[0];
  const image = getImage(campaign);
  const style = normalizeObject(campaign.style_settings);
  const tone = style.tone || "ivory";
  const align = style.align || "right";

  return (
    <section className={`campaign-hero campaign-tone-${tone} campaign-align-${align}`}>
      <div className="site-container">
        <div className="campaign-hero-shell">
          <div className="campaign-hero-content">
            <span className="campaign-kicker">Jooti Campaign</span>
            <h1>{campaign.title}</h1>
            {campaign.subtitle ? <p>{campaign.subtitle}</p> : null}
            {campaign.body ? <p className="campaign-hero-body">{campaign.body}</p> : null}
            <CampaignButton campaign={campaign} className="campaign-primary-link" />
          </div>

          <div className="campaign-hero-visual">
            {image ? (
              <img src={image} alt={campaign.title || campaign.name} />
            ) : (
              <div className="campaign-visual-placeholder">
                <strong>JOOTI</strong>
                <small>Fashion Editorial</small>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function CampaignBanner({ campaigns, placement = "home" }) {
  const banners = getCampaignsByType(campaigns, "banner", placement);
  if (!banners.length) return null;

  return (
    <section className="campaign-banners-section">
      <div className="site-container">
        <div className="campaign-banners-grid">
          {banners.map((campaign) => {
            const image = getImage(campaign);
            const style = normalizeObject(campaign.style_settings);
            const tone = style.tone || "soft";

            return (
              <div className={`campaign-banner-card campaign-tone-${tone}`} key={campaign.id}>
                <div className="campaign-banner-content">
                  <span className="campaign-kicker">Jooti Selection</span>
                  <h2>{campaign.title}</h2>
                  {campaign.subtitle ? <p>{campaign.subtitle}</p> : null}
                  <CampaignButton campaign={campaign} className="campaign-secondary-link" />
                </div>

                <div className="campaign-banner-visual">
                  {image ? (
                    <img src={image} alt={campaign.title || campaign.name} />
                  ) : (
                    <div className="campaign-visual-placeholder">
                      <strong>JOOTI</strong>
                      <small>Campaign Banner</small>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CampaignPopup({ campaigns, placement = "home" }) {
  const popups = useMemo(() => getCampaignsByType(campaigns, "popup", placement), [campaigns, placement]);
  const [activePopup, setActivePopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const popup = popups.find((item) => shouldShowCampaign(item));

    if (!popup) return undefined;

    const rules = normalizeObject(popup.display_rules);
    const delay = Number(rules.delay_ms || 900);

    const timer = setTimeout(() => {
      setActivePopup(popup);
      setIsVisible(true);
      markCampaignSeen(popup);
    }, delay);

    return () => clearTimeout(timer);
  }, [popups]);

  if (!activePopup || !isVisible) return null;

  const image = getImage(activePopup);
  const rules = normalizeObject(activePopup.display_rules);
  const style = normalizeObject(activePopup.style_settings);
  const tone = style.tone || "soft";
  const blur = Boolean(style.blur_background);
  const maxWidth = Number(style.max_width || 520);

  const closePopup = () => {
    setIsVisible(false);
  };

  const backdropClick = () => {
    if (rules.close_on_backdrop !== false) {
      closePopup();
    }
  };

  return (
    <div className={`campaign-popup-backdrop ${blur ? "campaign-popup-blur" : ""}`} onClick={backdropClick} role="presentation">
      <div
        className={`campaign-popup-card campaign-tone-${tone}`}
        style={{ maxWidth: `${maxWidth}px` }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={activePopup.title || "پیشنهاد ویژه جوتی"}
      >
        {style.show_close !== false ? (
          <button type="button" className="campaign-popup-close" onClick={closePopup} aria-label="بستن پاپ‌آپ">
            ×
          </button>
        ) : null}

        <div className="campaign-popup-media">
          {image ? (
            <img src={image} alt={activePopup.title || activePopup.name} />
          ) : (
            <div className="campaign-visual-placeholder">
              <strong>JOOTI</strong>
              <small>Fashion Popup</small>
            </div>
          )}
        </div>

        <div className="campaign-popup-content">
          <span className="campaign-kicker">Jooti Mood</span>
          <h2>{activePopup.title}</h2>
          {activePopup.subtitle ? <p>{activePopup.subtitle}</p> : null}
          {activePopup.body ? <p className="campaign-popup-body">{activePopup.body}</p> : null}
          <CampaignButton campaign={activePopup} className="campaign-primary-link" />
        </div>
      </div>
    </div>
  );
}

export default function CampaignRenderer({ campaigns, placement = "home", types = ["topbar", "hero", "banner", "popup"] }) {
  const normalizedCampaigns = campaigns || {};

  return (
    <>
      {types.includes("topbar") ? <CampaignTopbar campaigns={normalizedCampaigns} placement={placement} /> : null}
      {types.includes("hero") ? <CampaignHero campaigns={normalizedCampaigns} placement={placement} /> : null}
      {types.includes("banner") ? <CampaignBanner campaigns={normalizedCampaigns} placement={placement} /> : null}
      {types.includes("popup") ? <CampaignPopup campaigns={normalizedCampaigns} placement={placement} /> : null}
    </>
  );
}
